import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";

/**
 * Small "Volume II opens for early readers" badge.
 * Clicking opens a soft modal that captures email into newsletter with tag=vol2_waitlist.
 */
export default function Vol2WaitlistBadge() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email, name, tag: "vol2_waitlist" });
      toast.success(data.message || "You're on the list ✨");
      setJoined(true);
    } catch {
      toast.error("Could not save that. Try again?");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setOpen(false);
    setTimeout(() => { setJoined(false); setEmail(""); setName(""); }, 300);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 bg-gradient-to-r from-peach/40 to-lavender/40 hover:from-peach/60 hover:to-lavender/60 border border-peach-deep/30 text-ink/80 hover:text-lapis text-xs uppercase tracking-[0.18em] px-4 py-2 rounded-full font-semibold transition-all duration-300"
        data-testid="vol2-waitlist-badge"
      >
        <Sparkles size={12} className="text-peach-deep" /> Volume II opens for early readers · join the waitlist
      </button>

      {open && (
        <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[80] flex items-center justify-center p-4" data-testid="vol2-waitlist-modal">
          <div className="glass-strong rounded-[1.75rem] w-full max-w-md p-8 relative">
            <button onClick={close} className="absolute top-5 right-5 text-ink/60 hover:text-lapis" data-testid="vol2-waitlist-close">
              <X size={20} />
            </button>
            {joined ? (
              <div className="text-center py-8" data-testid="vol2-waitlist-thanks">
                <Sparkles size={36} className="text-peach-deep mx-auto mb-5" strokeWidth={1.3} />
                <h2 className="font-serif text-3xl text-lapis font-light italic">You're on the list.</h2>
                <p className="mt-4 text-ink/65 leading-relaxed">
                  I'll write the moment Volume II opens — you'll be among the first to read it.
                </p>
                <button onClick={close} className="btn-soft mt-8">Close</button>
              </div>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 bg-peach/30 text-peach-deep text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full font-semibold mb-5">
                  <Sparkles size={10} /> Early readers
                </div>
                <h2 className="font-serif text-3xl text-lapis font-light leading-tight">
                  Volume II — <em className="text-peach-deep">just for you</em>.
                </h2>
                <p className="mt-4 text-ink/65 leading-relaxed text-sm">
                  Drop your email and I'll write to you the moment Volume II is ready — before it goes anywhere else.
                  Nothing else. Just one quiet letter.
                </p>
                <form onSubmit={submit} className="mt-7 space-y-5" data-testid="vol2-waitlist-form">
                  <div>
                    <label className="overline block mb-2">Your name (optional)</label>
                    <input className="eth-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="vol2-waitlist-name" />
                  </div>
                  <div>
                    <label className="overline block mb-2">Email</label>
                    <input required type="email" className="eth-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="vol2-waitlist-email" />
                  </div>
                  <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="vol2-waitlist-submit">
                    {loading ? "Saving…" : "Save my spot"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
